import type { LayerState } from "./MapContextManager";
import { EventEmitter } from "eventemitter3";
import debounce from "lodash.debounce";
import type { Map as MapboxMap } from "mapbox-gl";

/**
 * Minimum time (ms) a layer must be loading before `loading` is exposed as
 * `true` via getState(). If a layer starts and finishes loading within this
 * window, downstream consumers never see a loading flash.
 */
export const LOADING_STATE_MIN_DURATION_MS = 700;

/**
 * Debounce interval (ms) for loading-related state change notifications.
 * Non-loading mutations (visibility, opacity, etc.) are notified immediately.
 */
const LOADING_NOTIFY_DEBOUNCE_MS = 100;

/**
 * Detects harmless raster-tile decode failures that mapbox-gl surfaces as map
 * `error` events.
 *
 * Raster overlays are served as PNG tiles, and sparse rasters frequently return
 * empty (0-byte) tile bodies for no-data areas. mapbox-gl decodes tiles with
 * `createImageBitmap`, and unlike its `HTMLImageElement` fallback, that code
 * path does NOT substitute a transparent PNG for empty data — it calls
 * `createImageBitmap()` on a zero-byte blob, which rejects. mapbox-gl then wraps
 * the rejection in a "Could not load image because of ..." error and emits it on
 * the map. The browser-specific underlying messages are:
 *   - Chrome:  "The source image could not be decoded"
 *   - Firefox: "An attempt was made to use an object that is not, or is no
 *     longer usable" (an `InvalidStateError` thrown for empty blobs)
 *
 * In both cases the layer still renders correctly from the tiles that do have
 * data, so surfacing an error indicator is a false positive. We ignore these so
 * the layer list doesn't show a spurious error (see seasketch/next#953).
 */
function isEmptyRasterTileDecodeError(message: string | undefined): boolean {
  if (!message) return false;
  return (
    /source image could not be decoded/i.test(message) ||
    // Firefox InvalidStateError raised by createImageBitmap() on empty tiles
    /attempt was made to use an object/i.test(message)
  );
}

/**
 * Compare two LayerState-like objects for equality. Every enumerable property
 * is compared with strict equality (`===`), which means `error` (an Error
 * object) and any other reference types are compared by identity — not by
 * deep value. This is intentional: Error instances keep the same identity
 * until explicitly replaced.
 */
function layerStateEqual<T extends LayerState>(a: T, b: T): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if ((a as any)[k] !== (b as any)[k]) return false;
  }
  return true;
}

/**
 * A general-purpose, synchronous layer state store that:
 *
 * 1. Suppresses transient loading states via a `loadingAt` timestamp
 *    threshold. If a layer loads in under `LOADING_STATE_MIN_DURATION_MS`,
 *    `loading` is never exposed as `true` to React.
 * 2. Maintains **referential stability**: `getState()` returns the same
 *    object reference when nothing meaningful has changed, preventing
 *    unnecessary React re-renders.
 *
 * MapContextManager keeps two instances:
 *   - overlays  → `LayerStateManager<LayerState>`   keyed by stableId
 *   - sketches  → `LayerStateManager<SketchLayerState>` keyed by string id
 *
 * **All mutations are immediate and synchronous.** Loading-related state
 * change notifications are debounced (10 ms); all other mutations notify
 * immediately. Subscribe to the `"stateChanged"` event to react to
 * effective state changes.
 */
export class LayerStateManager<TState extends LayerState> extends EventEmitter {
  // ── internal stores ──────────────────────────────────────────────────────

  /** Namespace prefix used to identify this manager's sources on the map. */
  readonly namespace: string;

  /** Raw mutable state. `loading` here is the *raw* value from the map. */
  private rawState: { [key: string]: TState } = {};

  /** Timestamp (ms) when a layer first started loading. */
  private loadingStartedAt: { [key: string]: number } = {};

  /** Map from mapbox sourceId → set of tracked layer keys using that source. */
  private sourceToKeys: { [sourceId: string]: Set<string> } = {};

  /** Keys excluded from automatic loading/error monitoring. */
  private excludedFromMonitoring = new Set<string>();

  /**
   * Snapshot returned by the last `getState()` call. Used for referential
   * stability — we compare the freshly-built state against this snapshot
   * and return the same reference when nothing changed.
   *
   * We *never* null this out on mutations so that `getState()` can always
   * run the comparison. The snapshot objects are spread-copies, so they are
   * not affected by subsequent in-place mutations to `rawState`.
   */
  private _lastEmitted: { [key: string]: TState } | null = null;

  /**
   * Snapshot reference at the time we last emitted `"stateChanged"`.
   * Used to avoid duplicate emissions when `getState()` returns the same
   * reference.
   */
  private _lastNotifiedRef: { [key: string]: TState } | null = null;

  private readonly loadingMinMs: number;
  private readonly pollIntervalMs: number;

  // ── map event monitoring ────────────────────────────────────────────────

  private map: MapboxMap | null = null;
  private pollTimerId: ReturnType<typeof setTimeout> | null = null;

  private boundOnData: ((e: any) => void) | null = null;
  private boundOnDataLoading: ((e: any) => void) | null = null;
  private boundOnError: ((e: any) => void) | null = null;

  constructor(
    namespace: string,
    initialState?: { [key: string]: TState },
    loadingMinMs = LOADING_STATE_MIN_DURATION_MS,
    pollIntervalMs = 750
  ) {
    super();
    this.namespace = namespace;
    this.loadingMinMs = loadingMinMs;
    this.pollIntervalMs = pollIntervalMs;
    if (initialState) {
      for (const key of Object.keys(initialState)) {
        this.rawState[key] = { ...initialState[key] };
      }
    }
  }

  /** Returns a Mapbox source ID prefixed with this manager's namespace. */
  prefixedSourceId(rawId: string | number): string {
    return `${this.namespace}-${rawId}`;
  }

  // ── state change notification ───────────────────────────────────────────

  /**
   * Check whether the effective public state has changed since the last
   * notification and, if so, emit `"stateChanged"`. Called immediately
   * after non-loading mutations.
   */
  private checkAndNotify = (): void => {
    const state = this.getState();
    if (state !== this._lastNotifiedRef) {
      this._lastNotifiedRef = state;
      this.emit("stateChanged", state);
    }
  };

  /**
   * Debounced variant of `checkAndNotify`, used exclusively after
   * loading-related mutations. 10 ms trailing-edge debounce prevents a
   * flood of context pushes while map sources are actively loading.
   */
  private debouncedCheckAndNotify = debounce(
    this.checkAndNotify,
    LOADING_NOTIFY_DEBOUNCE_MS
  );

  private shortDebouncedCheckAndNotify = debounce(this.checkAndNotify, 50);

  // ── map attachment & source monitoring ─────────────────────────────────

  /**
   * Attach to a Mapbox GL map instance to start monitoring source
   * loading/error state. Registers `data`, `dataloading`, and `error`
   * listeners filtered by this manager's namespace prefix.
   */
  attachToMap(map: MapboxMap): void {
    this.detachFromMap();
    this.map = map;

    this.boundOnData = (e: any) => this.onMapData(e);
    this.boundOnDataLoading = (e: any) => this.onMapDataLoading(e);
    this.boundOnError = (e: any) => this.onMapError(e);

    map.on("data", this.boundOnData);
    map.on("dataloading", this.boundOnDataLoading);
    map.on("error", this.boundOnError);

    // Kick the poll for any layers already in loading state (e.g. restored
    // from saved preferences before the map was created).
    this.startPollIfNeeded();
  }

  /**
   * Detach from the current map, removing all event listeners and stopping
   * any active poll timer.
   */
  detachFromMap(): void {
    if (this.map) {
      if (this.boundOnData) this.map.off("data", this.boundOnData);
      if (this.boundOnDataLoading)
        this.map.off("dataloading", this.boundOnDataLoading);
      if (this.boundOnError) this.map.off("error", this.boundOnError);
      this.boundOnData = null;
      this.boundOnDataLoading = null;
      this.boundOnError = null;
    }
    this.map = null;
    this.stopPoll();
  }

  /** Exclude a layer key from automatic loading/error monitoring. */
  excludeFromMonitoring(key: string): void {
    this.excludedFromMonitoring.add(key);
  }

  /** Re-include a previously excluded key in monitoring. */
  includeInMonitoring(key: string): void {
    this.excludedFromMonitoring.delete(key);
  }

  // ── Tier 1: event-driven monitoring ──────────────────────────────────────

  private isRelevantSource(sourceId: string): boolean {
    if (!sourceId) return false;
    if (!sourceId.startsWith(this.namespace + "-")) return false;
    return this.hasRelevanceToSource(sourceId);
  }

  private keysForSource(sourceId: string): string[] {
    const set = this.sourceToKeys[sourceId];
    if (!set) return [];
    return Array.from(set).filter((k) => !this.excludedFromMonitoring.has(k));
  }

  private onMapDataLoading = (event: any): void => {
    if (event.dataType !== "source") return;
    const sourceId: string = event.sourceId;
    if (!this.isRelevantSource(sourceId)) return;

    for (const key of this.keysForSource(sourceId)) {
      this.setLoading(key, true);
    }
    this.startPollIfNeeded();
  };

  private onMapData = (event: any): void => {
    if (event.dataType !== "source") return;
    const sourceId: string = event.sourceId;
    if (!this.isRelevantSource(sourceId)) return;
    if (!this.map) return;

    if (this.map.isSourceLoaded(sourceId)) {
      for (const key of this.keysForSource(sourceId)) {
        const allSourceIds = this.getSourceIdsForKey(key);
        const existingSources = allSourceIds.filter((id) =>
          this.map!.getSource(id)
        );
        if (existingSources.length === 0) continue;
        const allLoaded = existingSources.every((id) =>
          this.map!.isSourceLoaded(id)
        );
        if (allLoaded) {
          this.setLoading(key, false);
          if (this.rawState[key]?.error) {
            this.setError(key, undefined);
          }
        }
      }
    }
  };

  private onMapError = (event: any): void => {
    const sourceId: string | undefined = event.sourceId;
    if (!sourceId) return;
    if (!sourceId.startsWith(this.namespace + "-")) return;
    if (isEmptyRasterTileDecodeError(event.error?.message)) return;

    if (this.hasRelevanceToSource(sourceId)) {
      for (const key of this.keysForSource(sourceId)) {
        this.setError(key, event.error);
        this.setLoading(key, false);
      }
    }
  };

  // ── Tier 2: fallback polling ─────────────────────────────────────────────

  private hasAnyLoading(): boolean {
    for (const key of Object.keys(this.rawState)) {
      if (this.excludedFromMonitoring.has(key)) continue;
      if (this.rawState[key].loading) return true;
    }
    return false;
  }

  private startPollIfNeeded(): void {
    if (this.pollTimerId !== null) return;
    if (!this.hasAnyLoading()) return;
    if (!this.map) return;
    this.pollTimerId = setTimeout(
      () => this.pollLoadingStates(),
      this.pollIntervalMs
    );
  }

  private stopPoll(): void {
    if (this.pollTimerId !== null) {
      clearTimeout(this.pollTimerId);
      this.pollTimerId = null;
    }
  }

  private pollLoadingStates(): void {
    this.pollTimerId = null;
    if (!this.map) return;
    for (const key of Object.keys(this.rawState)) {
      if (this.excludedFromMonitoring.has(key)) continue;
      const raw = this.rawState[key];
      if (!raw?.loading) continue;
      const sourceIds = this.getSourceIdsForKey(key);
      if (sourceIds.length === 0) continue;
      const existingSources = sourceIds.filter((id) => this.map!.getSource(id));
      if (existingSources.length === 0) continue;
      const allLoaded = existingSources.every((id) =>
        this.map!.isSourceLoaded(id)
      );
      if (allLoaded) {
        this.setLoading(key, false);
        if (raw.error) this.setError(key, undefined);
      }
    }
    this.startPollIfNeeded();
  }

  /** Returns all registered Mapbox source IDs for a given layer key. */
  private getSourceIdsForKey(key: string): string[] {
    const ids: string[] = [];
    for (const sourceId in this.sourceToKeys) {
      if (this.sourceToKeys[sourceId].has(key)) {
        ids.push(sourceId);
      }
    }
    return ids;
  }

  /**
   * Cancel any pending debounced notification and remove all listeners.
   * Call when this manager is no longer needed.
   */
  destroy(): void {
    this.detachFromMap();
    this.debouncedCheckAndNotify.cancel();
    this.removeAllListeners();
  }

  // ── source-tracking (for event triage) ───────────────────────────────────

  /**
   * Register which mapbox sourceId a layer key maps to.
   * Call when adding/showing a layer so data events can be triaged.
   */
  setSourceForKey(key: string, sourceId: string): void {
    if (!this.sourceToKeys[sourceId]) {
      this.sourceToKeys[sourceId] = new Set();
    }
    this.sourceToKeys[sourceId].add(key);
  }

  removeSourceForKey(key: string, sourceId: string): void {
    this.sourceToKeys[sourceId]?.delete(key);
    if (this.sourceToKeys[sourceId]?.size === 0) {
      delete this.sourceToKeys[sourceId];
    }
  }

  /** Returns `true` if any tracked layer uses the given mapbox source. */
  hasRelevanceToSource(sourceId: string): boolean {
    const set = this.sourceToKeys[sourceId];
    return set !== undefined && set.size > 0;
  }

  // ── mutations (all synchronous, no debouncing) ───────────────────────────

  addLayer(key: string, state: TState): void {
    this.rawState[key] = { ...state };
    if (state.loading) {
      this.loadingStartedAt[key] = Date.now();
      this.startPollIfNeeded();
    }
    this.shortDebouncedCheckAndNotify();
  }

  removeLayer(key: string): void {
    delete this.rawState[key];
    delete this.loadingStartedAt[key];
    // Remove from source tracking
    for (const sourceId in this.sourceToKeys) {
      this.sourceToKeys[sourceId].delete(key);
      if (this.sourceToKeys[sourceId].size === 0) {
        delete this.sourceToKeys[sourceId];
      }
    }
    this.shortDebouncedCheckAndNotify();
  }

  has(key: string): boolean {
    return key in this.rawState;
  }

  keys(): string[] {
    return Object.keys(this.rawState);
  }

  /**
   * Get the **raw** (internal) state for a single layer.
   * Does NOT apply the loading threshold — use `getLayerState()` if you
   * need the effective/public value.
   */
  getRaw(key: string): TState | undefined {
    return this.rawState[key];
  }

  setLoading(key: string, loading: boolean): void {
    const s = this.rawState[key];
    if (!s) return;
    if (loading) {
      if (!(key in this.loadingStartedAt)) {
        this.loadingStartedAt[key] = Date.now();
      }
      s.loading = true;
      this.startPollIfNeeded();
    } else {
      delete this.loadingStartedAt[key];
      s.loading = false;
    }
    this.debouncedCheckAndNotify();
  }

  setVisible(key: string, visible: boolean): void {
    const s = this.rawState[key];
    if (!s) return;
    s.visible = visible;
    this.debouncedCheckAndNotify();
  }

  setOpacity(key: string, opacity: number | undefined): void {
    const s = this.rawState[key];
    if (!s) return;
    s.opacity = opacity;
    this.checkAndNotify();
  }

  setZOrderOverride(key: string, zOrder: number | undefined): void {
    const s = this.rawState[key];
    if (!s) return;
    s.zOrderOverride = zOrder;
    this.checkAndNotify();
  }

  setHidden(key: string, hidden: boolean): void {
    const s = this.rawState[key];
    if (!s) return;
    s.hidden = hidden;
    this.debouncedCheckAndNotify();
  }

  setError(key: string, error: Error | undefined): void {
    const s = this.rawState[key];
    if (!s) return;
    s.error = error;
    this.checkAndNotify();
  }

  /**
   * Patch arbitrary properties on a layer's state.
   * Useful for SketchLayerState extras (sketchClassId, filterMvtUrl, etc.).
   */
  patch(key: string, partial: Partial<TState>): void {
    const s = this.rawState[key];
    if (!s) return;
    for (const k of Object.keys(partial) as (keyof TState)[]) {
      (s as any)[k] = partial[k];
    }
    this.debouncedCheckAndNotify();
  }

  /**
   * Remove all layers whose keys are NOT in the provided set.
   * Returns `true` if any layers were removed.
   */
  retainOnly(keysToKeep: Set<string> | string[]): boolean {
    const keepSet =
      keysToKeep instanceof Set ? keysToKeep : new Set(keysToKeep);
    let changed = false;
    for (const key of Object.keys(this.rawState)) {
      if (!keepSet.has(key)) {
        // removeLayer already calls checkAndNotify for each removal,
        // but that's fine — checkAndNotify is cheap when nothing changed.
        this.removeLayer(key);
        changed = true;
      }
    }
    return changed;
  }

  // ── read — referential stability + loading threshold ─────────────────────

  /**
   * Build the effective public state, applying the loading threshold.
   * Returns the **same reference** as the previous call when every layer's
   * effective state is unchanged.
   */
  getState(): { [key: string]: TState } {
    const now = Date.now();
    const rawKeys = Object.keys(this.rawState);
    const next: { [key: string]: TState } = {};

    for (const key of rawKeys) {
      const raw = this.rawState[key];
      // Compute effective loading: only expose loading=true if raw is
      // loading AND it has been loading for at least loadingMinMs.
      let effectiveLoading = raw.loading;
      if (raw.loading && key in this.loadingStartedAt) {
        effectiveLoading =
          now - this.loadingStartedAt[key] >= this.loadingMinMs;
      }
      // Always create a snapshot copy so _lastEmitted is never affected
      // by later in-place mutations to rawState.
      next[key] = { ...raw, loading: effectiveLoading };
    }

    // Referential stability: if the effective state is identical to the
    // previous snapshot, return the previous reference.
    if (this._lastEmitted !== null) {
      const prevKeys = Object.keys(this._lastEmitted);
      if (prevKeys.length === rawKeys.length) {
        let allEqual = true;
        for (const k of rawKeys) {
          if (
            !(k in this._lastEmitted) ||
            !layerStateEqual(this._lastEmitted[k], next[k])
          ) {
            allEqual = false;
            break;
          }
        }
        if (allEqual) {
          return this._lastEmitted;
        }
      }
    }
    this._lastEmitted = next;
    return next;
  }

  /**
   * Get the effective state for a single layer (loading threshold applied).
   */
  getLayerState(key: string): TState | undefined {
    const raw = this.rawState[key];
    if (!raw) return undefined;
    let effectiveLoading = raw.loading;
    if (raw.loading && key in this.loadingStartedAt) {
      effectiveLoading =
        Date.now() - this.loadingStartedAt[key] >= this.loadingMinMs;
    }
    // Return a snapshot copy
    return { ...raw, loading: effectiveLoading };
  }
}
